import { Suspense, useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/I18nProvider'
import { homeFor, useApp } from '../store/AppProvider'
import { api, useApi } from '../lib/api'
import { Avatar, BrandMark, Button, Icon, IconButton, Input, Modal, Spinner, Thumb } from './ui'
import { relative } from '../lib/format'
import { formatCredits } from '../lib/credits'
import { RouteError } from './RouteError'

/* --------------------------------------------------------------- logo --- */
export const Logo = ({ light = false, size = 'md' }) => (
  <Link to="/" className="flex items-center gap-2.5 shrink-0">
    <BrandMark size={size === 'sm' ? 30 : 36} />
    <span className={`font-extrabold tracking-[-0.02em] ${size === 'sm' ? 'text-[16px]' : 'text-[19px]'} ${light ? 'text-white' : 'text-ink'}`}>
      KnoWledge<span className="text-brand">X</span>
    </span>
  </Link>
)

/* ------------------------------------------------------- lang switcher --- */
export function LangSwitcher({ light }) {
  const { locale, setLocale, locales } = useI18n()
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const h = () => setOpen(false)
    if (open) document.addEventListener('click', h)
    return () => document.removeEventListener('click', h)
  }, [open])
  const cur = locales.find((l) => l.code === locale)
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 h-9 px-2.5 rounded-xl text-[13px] font-extrabold transition ${light ? 'text-white/90 hover:bg-white/10' : 'text-fg hover:bg-black/5'}`}>
        <Icon name="language" size={17} />{cur.short}<Icon name="expand_more" size={15} />
      </button>
      {open && (
        <div className="absolute end-0 mt-1.5 w-52 bg-white rounded-2xl shadow-pop border border-line py-1.5 z-50">
          {locales.map((l) => (
            <button key={l.code} onClick={() => { setLocale(l.code); setOpen(false) }}
              className={`w-full flex items-center justify-between px-3.5 py-2 text-sm hover:bg-surface-sub ${l.code === locale ? 'font-extrabold text-brand' : ''}`}>
              <span className={l.code === 'ar' ? 'font-arabic' : ''}>{l.label}</span>
              {l.code === locale && <Icon name="check" size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------ variant switcher --- */
/**
 * Alat bantu review desain: menukar tampilan beranda / kartu / player secara
 * langsung.
 *
 * Default MATI. Ini tombol untuk sesi desain internal, bukan fitur pengunjung —
 * dan sejak situs berisi data nyata, tombol melayang "Varian desain" di setiap
 * halaman membuat produk terasa masih maket. Nyalakan dengan
 * `VITE_DESIGN_VARIANTS=true` di `.env.local` saat memang sedang membandingkan
 * tampilan. Pilihan yang tersimpan di browser tetap dihormati; yang hilang
 * hanya tombolnya.
 */
const VARIANTS_ENABLED = import.meta.env.VITE_DESIGN_VARIANTS === 'true'

export function VariantSwitcher() {
  const { variant, setVariant } = useApp()
  const [open, setOpen] = useState(false)
  if (!VARIANTS_ENABLED) return null
  const groups = [
    { key: 'home', label: 'Beranda', opts: [['a', 'Hero gelap + rail'], ['b', 'Editorial split'], ['c', 'Kompak / mobile-first']] },
    { key: 'card', label: 'Kartu kelas', opts: [['a', 'Marketplace'], ['b', 'Editorial'], ['c', 'Baris padat']] },
    { key: 'player', label: 'Player', opts: [['a', 'Teater + silabus kanan'], ['b', 'Fokus + silabus drawer'], ['c', 'Split catatan']] },
  ]
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="fixed z-[60] bottom-4 end-4 h-11 ps-3.5 pe-4 rounded-full bg-ink text-white text-[13px] font-extrabold shadow-pop flex items-center gap-2 hover:bg-ink-deep">
        <Icon name="tune" size={18} />Varian desain
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Varian desain" width={440}>
        <p className="text-sm text-muted mb-4 kx-pretty">Bandingkan pendekatan tanpa keluar dari alur. Pilihan disimpan di browser Anda.</p>
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key}>
              <p className="text-[13px] font-extrabold mb-2">{g.label}</p>
              <div className="grid gap-1.5">
                {g.opts.map(([val, label]) => (
                  <button key={val} onClick={() => setVariant(g.key, val)}
                    className={`flex items-center gap-2.5 px-3 h-10 rounded-xl text-start text-sm font-bold border transition ${variant[g.key] === val ? 'border-brand bg-brand-soft text-brand-deep' : 'border-line hover:border-ink/30'}`}>
                    <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-black ${variant[g.key] === val ? 'bg-brand text-white' : 'bg-black/10'}`}>{val.toUpperCase()}</span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  )
}

/* ---------------------------------------------------------- login modal --- */
/**
 * SATU PINTU: SSO The Muslim. Tidak ada form email+password KnowledgeX.
 *
 * The Muslim memiliki perakunan (CLAUDE.md §12), jadi di sanalah orang
 * mendaftar, memasang password, dan memverifikasi emailnya. KnowledgeX tidak
 * pernah membuat identitas — ia mencerminkan.
 *
 * Form lokal dan pemilih akun demo sudah DIHAPUS, bukan disembunyikan. Sebelum
 * ini bisa dilakukan, 38 akun yang passwordnya hanya hidup di KnowledgeX (32
 * pakar, superadmin, dua akun uji) dipindahkan ke The Muslim oleh
 * `cmd/ssomigrate` — dengan hash yang sama, jadi password yang sudah dibagikan
 * tetap berlaku. Urutan itu yang membuat penghapusan ini aman; membaliknya
 * berarti tidak ada satu pun orang yang bisa mengurus platform ini.
 *
 * Kalau suatu hari SSO mati, jalan keluarnya BUKAN menghidupkan form ini lagi.
 * Ia akun darurat di The Muslim — satu tempat yang menyimpan cara masuk.
 */
export function LoginModal() {
  const { loginOpen, setLoginOpen, startSSO } = useApp()
  const { t } = useI18n()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const goSSO = async () => {
    setBusy(true); setErr(null)
    try {
      await startSSO()
      // Sengaja tidak ada setBusy(false) di jalur sukses: halamannya sedang
      // berpindah, dan mengembalikan tombol ke keadaan semula membuatnya
      // terlihat seperti tidak terjadi apa-apa.
    } catch (e) {
      setErr(e.message); setBusy(false)
    }
  }

  return (
    <Modal open={loginOpen} onClose={() => setLoginOpen(false)} width={420}>
      <div className="text-center mb-5">
        <BrandMark size={56} className="mx-auto mb-3" />
        <h3 className="text-xl font-extrabold">{t('auth.loginWith')}</h3>
        <p className="text-sm text-muted mt-1 kx-pretty">{t('auth.subtitle')}</p>
      </div>
      <Button className="w-full" loading={busy} icon="account_circle" onClick={goSSO}>
        {t('auth.ssoContinue')}
      </Button>
      <p className="text-[12px] text-muted mt-3 text-center kx-pretty">{t('auth.ssoHint')}</p>
      {err && <p className="text-[12px] text-danger mt-3 text-center kx-pretty">{err}</p>}
    </Modal>
  )
}

/* ------------------------------------------------------------- search --- */
function SearchBox({ className = '' }) {
  const { t } = useI18n()
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const [res, setRes] = useState(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (q.length < 2) { setRes(null); return }
    const id = setTimeout(async () => {
      try { setRes(await api.get('/search', { q })) } catch { setRes(null) }
    }, 300)
    return () => clearTimeout(id)
  }, [q])

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={(e) => { e.preventDefault(); setOpen(false); nav(`/courses?search=${encodeURIComponent(q)}`) }}>
        <Icon name="search" size={19} className="absolute start-3.5 top-1/2 -translate-y-1/2 text-muted" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true) }} onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder={t('catalog.search')}
          className="w-full h-11 rounded-xl bg-surface-sub border border-line ps-11 pe-3 text-[14px] outline-none focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/15 transition" />
      </form>
      {open && res && (
        <div className="absolute top-12 left-0 right-0 bg-white rounded-2xl shadow-pop border border-line p-2 z-50 max-h-[70vh] overflow-auto">
          {!res.courses.length && !res.instructors.length && <p className="text-sm text-muted p-3">Tidak ada hasil untuk “{q}”.</p>}
          {res.courses.map((c) => (
            <Link key={c.id} to={`/courses/${c.slug}`} className="flex gap-3 items-center p-2 rounded-xl hover:bg-surface-sub">
              <Thumb src={c.thumbnail_url} className="w-14 shrink-0" radius={8} />
              <span className="min-w-0"><span className="block text-sm font-bold truncate text-fg">{c.title}</span>
                <span className="block text-[11px] text-muted">{c.instructor?.name}</span></span>
            </Link>
          ))}
          {res.instructors.map((i) => (
            <Link key={i.id} to={`/instructor/${i.slug}`} className="flex gap-3 items-center p-2 rounded-xl hover:bg-surface-sub">
              <Avatar src={i.avatar_url} name={i.name} size={34} verified={i.is_verified} />
              <span className="min-w-0"><span className="block text-sm font-bold truncate text-fg">{i.name}</span>
                <span className="block text-[11px] text-muted truncate">{i.headline}</span></span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------- header --- */
function NotifBell() {
  const [open, setOpen] = useState(false)
  const list = useApi('/me/notifications')
  const unread = (list.data || []).filter((n) => !n.read_at).length
  return (
    <div className="relative hidden sm:block" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)} className="relative w-10 h-10 grid place-items-center rounded-xl hover:bg-black/5">
        <Icon name="notifications" size={21} />
        {unread > 0 && <span className="absolute top-1.5 end-1.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-black grid place-items-center">{unread}</span>}
      </button>
      {open && (
        <div className="absolute end-0 mt-1.5 w-80 bg-white rounded-2xl shadow-pop border border-line z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <span className="font-extrabold text-sm">Notifikasi</span>
            <button className="text-[12px] font-bold text-brand" onClick={async () => { await api.post('/me/notifications/read'); list.refetch() }}>Tandai dibaca</button>
          </div>
          <div className="max-h-80 overflow-auto divide-y divide-line">
            {(list.data || []).map((n) => (
              <div key={n.id} className={`px-4 py-3 ${n.read_at ? '' : 'bg-brand-soft/40'}`}>
                <p className="text-[13px] font-extrabold">{n.title}</p>
                <p className="text-[12px] text-muted kx-pretty">{n.body}</p>
                <p className="text-[11px] text-muted mt-1">{relative(n.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Pemilih bahasa versi DATAR, untuk dipakai di dalam menu avatar.
 *
 * Sengaja bukan `LangSwitcher`: yang itu dropdown, dan dropdown di dalam
 * dropdown berarti klik pertama menutup induknya. Empat bahasa muat sebagai
 * baris chip, jadi tidak ada alasan menumpuk dua lapis menu.
 */
function LangChips() {
  const { locale, setLocale, locales } = useI18n()
  return (
    <div className="flex gap-1">
      {locales.map((l) => (
        <button key={l.code} onClick={() => setLocale(l.code)}
          title={l.label}
          className={`flex-1 py-1.5 rounded-lg text-[12px] font-extrabold transition ${
            l.code === locale ? 'bg-brand text-white' : 'bg-surface-sub text-muted hover:text-fg'}`}>
          {l.short}
        </button>
      ))}
    </div>
  )
}

/**
 * Menu avatar. Diekspor karena AdminLayout memakai yang SAMA PERSIS.
 *
 * Disalin ke dua tempat, dua menu itu perlahan menyimpang — satu dapat entri
 * baru, satunya tidak — dan tidak ada satu langkah pun yang terlihat salah saat
 * itu terjadi. Yang terlihat cuma "menu di panel admin kok beda".
 *
 * Saldo TIDAK dirender di sini lagi. Dompetnya milik The Muslim, dan angka
 * yang dibaca lintas layanan tiap kali menu dibuka adalah angka yang bisa
 * basi — kreditnya bisa berubah dari sub-app lain. Yang ada: tautan ke
 * halamannya, tempat angkanya datang dari satu sumber.
 */
export function UserMenu() {
  const { user, logout, isAdmin, isInstructor } = useApp()
  const { t, locale } = useI18n()
  const loc = useLocation()
  const [open, setOpen] = useState(false)
  // Alamat halaman akun The Muslim datang dari SERVER, tidak pernah dirakit di
  // sini. Ia berbeda antara dev, staging, dan prod; alamat yang dikarang klien
  // akan mengantar orang ke The Muslim yang salah — yang akunnya memang ada,
  // tapi bukan akun yang sedang ia pakai. Kosong = tautannya tidak dirender.
  const cfg = useApi('/auth/config')
  // Saldo dibaca dari bank lewat API kita. Kegagalannya SENGAJA tidak
  // ditangani di sini: `useApi` menyisakan `data` undefined, barisnya tidak
  // dirender, dan sisa menu tetap hidup. Angka di sudut layar tidak boleh
  // menjatuhkan menu yang isinya termuat baik-baik saja.
  const wallet = useApi('/me/credits')
  const accountUrl = cfg.data?.account_url || ''
  const creditsUrl = cfg.data?.credits_url || ''
  const affiliateUrl = cfg.data?.affiliate_url || ''
  const payoutsUrl = cfg.data?.payouts_url || ''
  useEffect(() => {
    const h = () => setOpen(false)
    if (open) document.addEventListener('click', h)
    return () => document.removeEventListener('click', h)
  }, [open])
  const items = [
    // Experts keep the learner links too — a pakar may well enrol in a class.
    ...(isInstructor ? [['/me/profile', 'badge', t('nav.profile')]] : []),
    ['/me', 'school', t('nav.myLearning')],
    ['/me/certificates', 'workspace_premium', t('nav.certificates')],
    ['/me/bookings', 'event', t('nav.bookings')],
    ['/me/orders', 'receipt_long', t('nav.orders')],
    // Pakar sekarang punya ruang kerja di /admin juga, bukan hanya admin.
    ...(isAdmin || isInstructor ? [['/admin', 'admin_panel_settings', t(isInstructor && !isAdmin ? 'admin.workspaceOnly' : 'nav.admin')]] : []),
    // Jalan PULANG ke storefront, dan ia harus ada DI MENU — bukan hanya di
    // bar atas. Tombol di `AdminLayout` ber-`hidden sm:inline-flex`, jadi di
    // ponsel tidak ada satu pun tempat di layar yang membawa orang keluar dari
    // panel: sidebar-nya drawer, dan tautan Logo-nya menuju /admin. Jerat §19
    // yang sama, arah sebaliknya.
    //
    // Dirender hanya saat sedang DI panel. Menampilkan "Storefront" sementara
    // orangnya sudah berdiri di storefront adalah entri yang tidak melakukan
    // apa pun, dan entri semacam itu membuat menu terasa tidak bisa dipercaya.
    ...(loc.pathname.startsWith('/admin') ? [['/', 'storefront', t('adm.nav.storefront')]] : []),
  ]
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 ps-1 pe-2 h-10 rounded-xl hover:bg-black/5">
        <Avatar src={user.avatar_url} name={user.name} size={32} />
        <Icon name="expand_more" size={16} className="text-muted" />
      </button>
      {open && (
        <div className="absolute end-0 mt-1.5 w-72 bg-white rounded-2xl shadow-pop border border-line py-1.5 z-50 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-2.5">
            <p className="font-extrabold text-sm truncate">{user.name}</p>
            <p className="text-[11px] text-muted truncate">{user.email}</p>
          </div>

          {/* Uang tinggal di The Muslim. Tautan KELUAR, bukan rute internal.
              Dirender hanya kalau server memberi alamatnya — satu tautan yang
              mengarah ke mana-mana lebih buruk daripada tautan yang tidak ada,
              karena yang kedua bisa dilaporkan sementara yang pertama cuma
              mendarat di halaman kosong milik orang lain.

              Dompet dan afiliasi untuk SEMUA peran: siapa pun yang membeli
              punya saldo, dan siapa pun boleh jadi afiliator. Pencairan hanya
              untuk yang bisa punya komisi. */}
          {/* Saldo DIBACA dari bank, tidak pernah disimpan di sini.
              §31 membuangnya dengan alasan yang benar — angka yang disalin ke
              KnowledgeX bisa basi, dan saldo basi terlihat seperti pembelian
              yang tidak terpotong. Yang salah bukan menampilkannya, tapi
              menyimpannya.

              `available: false` menutup TIGA sebab sekaligus (akun belum
              tertaut, bank belum dikonfigurasi, bank tidak terjangkau) dan
              ketiganya diperlakukan sama: barisnya tidak dirender. JANGAN
              menggantinya dengan "0,00" — nol yang dikarang terbaca persis
              seperti kredit yang hilang, dan orang yang melihatnya akan
              mencari uangnya alih-alih mencari tautan akunnya. */}
          {wallet.data?.available && (
            <a href={wallet.data.credits_url || creditsUrl} target="_blank" rel="noopener"
              onClick={() => setOpen(false)}
              className="flex items-baseline gap-2 mx-2 mb-1 px-3 py-2.5 rounded-xl bg-surface-sub hover:bg-black/5">
              <span className="text-[11px] font-bold text-muted">{t('wal.balance')}</span>
              <span className="ms-auto text-[15px] font-extrabold tabular-nums">
                {formatCredits(wallet.data.balance, locale)}
              </span>
            </a>
          )}

          {creditsUrl && (
            <div className="border-t border-line my-1 pt-1">
              {[
                [creditsUrl, 'account_balance_wallet', t('nav.wallet')],
                [affiliateUrl, 'share', t('nav.affiliate')],
                ...(isAdmin || isInstructor ? [[payoutsUrl, 'payments', t('wd.title')]] : []),
              ].filter(([href]) => href).map(([href, icon, label]) => (
                <a key={href} href={href} target="_blank" rel="noopener"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-[14px] font-bold hover:bg-black/5">
                  <Icon name={icon} size={19} className="text-muted" />{label}
                  <Icon name="open_in_new" size={15} className="text-muted ms-auto" />
                </a>
              ))}
            </div>
          )}

          {/* Identitas (nama, email, foto, password) dimiliki The Muslim, bukan
              KnowledgeX — lihat §12. Karena itu ini tautan KELUAR, bukan form:
              menyediakan form di sini berarti orang mengedit namanya, penukaran
              token berikutnya mengembalikannya, dan itu terbaca sebagai bug
              yang mustahil dilacak. */}
          {accountUrl && (
            <a href={accountUrl} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-fg hover:bg-surface-sub border-t border-line">
              <Icon name="manage_accounts" size={18} className="text-muted" />
              <span className="flex-1">{t('nav.account')}</span>
              <Icon name="open_in_new" size={14} className="text-muted" />
            </a>
          )}

          <div className="border-t border-line mt-1 pt-1">
            {items.map(([to, icon, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-fg hover:bg-surface-sub">
                <Icon name={icon} size={18} className="text-muted" />{label}
              </Link>
            ))}
          </div>

          {/* Bahasa turun ke sini dari bar atas. Ia tetap dirender untuk TAMU
              di header — orang yang belum masuk tidak punya menu ini, dan
              memilih bahasa adalah hal pertama yang mereka lakukan. */}
          <div className="px-3 py-2.5 border-t border-line mt-1">
            <p className="mb-1.5 text-[11px] font-bold text-muted flex items-center gap-1.5">
              <Icon name="language" size={15} />{t('nav.language')}
            </p>
            <LangChips />
          </div>

          <button onClick={() => { setOpen(false); logout() }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-danger hover:bg-danger/5 border-t border-line">
            <Icon name="logout" size={18} />{t('auth.logout')}
          </button>
        </div>
      )}
    </div>
  )
}

export function Header() {
  const { t } = useI18n()
  const { isAuthed, setLoginOpen } = useApp()
  const [mobile, setMobile] = useState(false)
  const loc = useLocation()
  useEffect(() => setMobile(false), [loc.pathname])
  // Saldo TIDAK ditampilkan di header lagi.
  //
  // Dompetnya milik The Muslim sekarang, jadi menampilkannya di sini berarti
  // satu panggilan lintas layanan di SETIAP pindah halaman, dan angka yang
  // bisa basi karena kreditnya bisa berubah dari sub-app lain. Angka basi di
  // header lebih buruk daripada tidak ada angka: ia terlihat seperti
  // pembelian yang tidak terpotong.
  //
  // Menu avatar menautkan ke halaman kreditnya; angkanya dibaca di sana, dari
  // satu sumber.

  const nav = [
    ['/courses', t('nav.catalog')], ['/instructors', t('nav.instructors')],
    ['/live', t('nav.live')], ['/qa', t('nav.qa')],
    ['/consultation', t('nav.consult')], ['/providers', t('nav.providers')],
  ]
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-line">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-0.5">
          {nav.map(([to, label]) => (
            <NavLink key={to} to={to} className={({ isActive }) => `px-3 py-2 rounded-xl text-[14px] font-bold transition ${isActive ? 'text-brand bg-brand-soft' : 'text-fg hover:bg-black/5'}`}>{label}</NavLink>
          ))}
        </nav>
        <SearchBox className="flex-1 max-w-md hidden md:block ms-auto" />
        <div className="flex items-center gap-1 ms-auto md:ms-0">
          {/* Bahasa, saldo kredit, dan "Belajar Saya" pindah ke dalam menu
              avatar. Bar atas menyisakan lonceng + avatar saja.

              Pemilih bahasa TETAP di sini untuk tamu: mereka tidak punya menu
              avatar, dan memilih bahasa adalah hal pertama yang dilakukan
              orang yang tidak berbahasa Indonesia. Menyembunyikannya di balik
              login berarti halaman depan hanya bisa dibaca setelah masuk. */}
          {isAuthed ? (
            <>
              <NotifBell />
              <UserMenu />
            </>
          ) : (
            <>
              <LangSwitcher />
              <Button size="sm" icon="login" onClick={() => setLoginOpen(true)}>{t('auth.login')}</Button>
            </>
          )}
          <IconButton icon={mobile ? 'close' : 'menu'} label="menu" kind="ghost" className="lg:hidden" onClick={() => setMobile((m) => !m)} />
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-line bg-white px-4 py-3 space-y-1 animate-fade-up">
          <SearchBox className="mb-2 md:hidden" />
          {nav.map(([to, label]) => (
            <NavLink key={to} to={to} className="block px-3 py-2.5 rounded-xl text-[15px] font-bold text-fg hover:bg-surface-sub">{label}</NavLink>
          ))}
          <NavLink to="/verify" className="block px-3 py-2.5 rounded-xl text-[15px] font-bold text-fg hover:bg-surface-sub">{t('nav.verify')}</NavLink>
        </div>
      )}
    </header>
  )
}

export function Footer() {
  const { t } = useI18n()
  const cols = [
    ['KnoWledgeX', [['/courses', t('nav.catalog')], ['/instructors', t('nav.instructors')], ['/live', t('nav.live')], ['/qa', t('nav.qa')], ['/consultation', t('nav.consult')]]],
    ['Untuk lembaga', [['/providers', t('nav.providers')], ['/admin', t('nav.admin')], ['#', 'Jadi provider']]],
    ['Bantuan', [['/verify', t('nav.verify')], ['#', 'Pusat bantuan'], ['#', 'Kebijakan refund'], ['#', 'Syarat & privasi']]],
  ]
  return (
    <footer className="mt-16 kx-hero-grad text-white/80">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Logo light />
          <p className="text-sm mt-3 max-w-xs kx-pretty">{t('hero.eyebrow')}.</p>
          <p className="text-[12px] mt-4 text-white/50">Bagian dari ekosistem The Muslim.</p>
        </div>
        {cols.map(([title, links]) => (
          <div key={title}>
            <p className="font-extrabold text-white text-sm mb-3">{title}</p>
            <ul className="space-y-2 text-sm">
              {links.map(([to, label]) => <li key={label}><Link to={to} className="text-white/75 hover:text-white">{label}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 py-5 text-center text-[12px] text-white/50">© 2026 KnoWledgeX · Mock data — backend Go menyusul</div>
    </footer>
  )
}

export function PublicLayout() {
  const loc = useLocation()
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <RouteError key={loc.pathname}>
          <Suspense fallback={<div className="py-24 grid place-items-center text-muted"><Spinner size={26} /></div>}>
            <Outlet />
          </Suspense>
        </RouteError>
      </main>
      <Footer />
      <VariantSwitcher />
    </div>
  )
}
